import React, {
  FC,
  Fragment,
  useCallback,
  useContext,
  useRef,
  MutableRefObject,
} from "react";
import { Pagination, Table } from "rsuite";
import { useTableSize } from "../../../../hook/width";
import { TableInstance } from "rsuite/Table";
import { TableColumnContext } from "../../../context/TableContext";
import { faCalendarXmark } from "@fortawesome/free-solid-svg-icons";
import { EmptyState } from "../../../components/state/empty";
import { useDataPager } from "../../../../hook/pager";
import { UserVisitControl } from "../../../../domain/control/Visit";
import { VisitControlTableTimestampCell } from "./timestamp";
import { VisitControlTableUnityCell } from "./unity";
import { useStyleContext } from "../../../context/StyleContext";
import { VisitControlTableColumns } from "../../../../schema/table/VisitControlTable";
import { VisitGroupTableActionCell } from "./action";

type Props = {
  visitControlList?: UserVisitControl[];
  name: string;
  onSelect: (item: UserVisitControl) => void;
  onPreviewClicked?: (item: UserVisitControl) => void;
};

export const VisitControlTable: FC<Props> = ({
  visitControlList,
  name,
  onSelect,
  onPreviewClicked,
}) => {
  const actionRef = useRef<string | null>(null) as MutableRefObject<
    string | null
  >;
  const tableRef = useRef<TableInstance<any, any>>(null);
  const tableSize = useTableSize(tableRef);
  const tableContext = useContext(TableColumnContext);
  const tablePreferences =
    tableContext.getTablePreferences<VisitControlTableColumns>(name);
  const { tableHeaderStyleSecondary } = useStyleContext();
  /* eslint-disable */

  const { partialData, changePage, page, changeLimit, limits, limit } =
    useDataPager({
      id: `vis_table_${name}`,
      items: visitControlList,
    });

  const onRowSelect = useCallback(
    (item: UserVisitControl) => {
      if (actionRef.current && onPreviewClicked) onPreviewClicked(item);
      else onSelect(item);
      actionRef.current = null;
    },
    [partialData]
  );

  const onPreviewActionSelected = useCallback(() => {
    actionRef.current = "preview";
  }, [partialData]);

  /* eslint-enable */

  return (
    <Fragment>
      <Table
        className={"rounded md:rounded-xl"}
        autoHeight
        data={partialData}
        ref={tableRef}
        renderEmpty={() => (
          <EmptyState
            icon={faCalendarXmark}
            title={"No hay registros de control de visitas"}
            message={
              "No se encontraron registros de control de visitas para la fecha seleccionada."
            }
          />
        )}
        onRowClick={onRowSelect as any}
        cellBordered
      >
        {tablePreferences.map((column) => {
          switch (column.name) {
            case "timestamp":
              return (
                <Table.Column
                  verticalAlign={"middle"}
                  align={"center"}
                  key={`column-${column.name}`}
                  width={tableSize.calculateWeight(0.1)}
                  onResize={(columnWidth) => {
                    if (columnWidth) {
                      tableContext.updateTableColumnWeight<VisitControlTableColumns>(
                        name,
                        column.name,
                        tableSize.sizeToWeight(columnWidth)
                      );
                    }
                  }}
                >
                  <Table.HeaderCell style={tableHeaderStyleSecondary}>
                    {column.label}
                  </Table.HeaderCell>
                  <VisitControlTableTimestampCell />
                </Table.Column>
              );
            case "unityId":
              return (
                <Table.Column
                  verticalAlign={"middle"}
                  align={"center"}
                  key={`column-${column.name}`}
                  width={tableSize.calculateWeight(0.2)}
                  onResize={(columnWidth) => {
                    if (columnWidth) {
                      tableContext.updateTableColumnWeight<VisitControlTableColumns>(
                        name,
                        column.name,
                        tableSize.sizeToWeight(columnWidth)
                      );
                    }
                  }}
                >
                  <Table.HeaderCell style={tableHeaderStyleSecondary}>
                    {column.label}
                  </Table.HeaderCell>
                  <VisitControlTableUnityCell />
                </Table.Column>
              );
            case "name":
              return (
                <Table.Column
                  verticalAlign={"middle"}
                  align={"center"}
                  key={`column-${column.name}`}
                  width={tableSize.calculateWeight(0.2)}
                  onResize={(columnWidth) => {
                    if (columnWidth) {
                      tableContext.updateTableColumnWeight<VisitControlTableColumns>(
                        name,
                        column.name,
                        tableSize.sizeToWeight(columnWidth)
                      );
                    }
                  }}
                >
                  <Table.HeaderCell style={tableHeaderStyleSecondary}>
                    {column.label}
                  </Table.HeaderCell>
                  <Table.Cell dataKey={"name"} />
                </Table.Column>
              );
            case "lastname":
              return (
                <Table.Column
                  verticalAlign={"middle"}
                  align={"center"}
                  key={`column-${column.name}`}
                  width={tableSize.calculateWeight(0.2)}
                  onResize={(columnWidth) => {
                    if (columnWidth) {
                      tableContext.updateTableColumnWeight<VisitControlTableColumns>(
                        name,
                        column.name,
                        tableSize.sizeToWeight(columnWidth)
                      );
                    }
                  }}
                >
                  <Table.HeaderCell style={tableHeaderStyleSecondary}>
                    {column.label}
                  </Table.HeaderCell>
                  <Table.Cell dataKey={"lastname"} />
                </Table.Column>
              );
            case "dni":
              return (
                <Table.Column
                  verticalAlign={"middle"}
                  align={"center"}
                  key={`column-${column.name}`}
                  width={tableSize.calculateWeight(0.1)}
                  onResize={(columnWidth) => {
                    if (columnWidth) {
                      tableContext.updateTableColumnWeight<VisitControlTableColumns>(
                        name,
                        column.name,
                        tableSize.sizeToWeight(columnWidth)
                      );
                    }
                  }}
                >
                  <Table.HeaderCell style={tableHeaderStyleSecondary}>
                    {column.label}
                  </Table.HeaderCell>
                  <Table.Cell dataKey={"dni"} />
                </Table.Column>
              );
            case "entry":
              return (
                <Table.Column
                  align={"center"}
                  verticalAlign={"middle"}
                  key={`column-${column.name}`}
                  width={tableSize.calculateWeight(0.1)}
                  onResize={(columnWidth) => {
                    if (columnWidth) {
                      tableContext.updateTableColumnWeight<VisitControlTableColumns>(
                        name,
                        column.name,
                        tableSize.sizeToWeight(columnWidth)
                      );
                    }
                  }}
                >
                  <Table.HeaderCell style={tableHeaderStyleSecondary}>
                    {column.label}
                  </Table.HeaderCell>
                  <Table.Cell dataKey={"entry"} />
                </Table.Column>
              );
            default:
              return (
                <Table.Column
                  align={"center"}
                  verticalAlign={"middle"}
                  width={tableSize.calculateWeight(column.weight)}
                  key={`columns-${column.name}`}
                >
                  <Table.HeaderCell style={tableHeaderStyleSecondary}>
                    Acciones
                  </Table.HeaderCell>
                  <VisitGroupTableActionCell
                    onPreviewActionClicked={onPreviewActionSelected}
                  />
                </Table.Column>
              );
          }
        })}
      </Table>

      {visitControlList ? (
        <div className={"py-4 flex flex-col gap-4"}>
          <span className={"text-md"}>
            Mostrando {partialData?.length} elementos / Total{" "}
            {visitControlList.length}
          </span>
          <Pagination
            prev
            next
            first
            last
            ellipsis
            boundaryLinks
            maxButtons={5}
            size="xs"
            layout={["limit", "|", "pager", "skip"]}
            total={visitControlList.length}
            limitOptions={limits}
            limit={limit}
            activePage={page}
            onChangePage={changePage}
            onChangeLimit={changeLimit}
          />
        </div>
      ) : null}
    </Fragment>
  );
};
